import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneNumber } from 'react-phone-number-input';
import styled from 'styled-components';
import * as Yup from 'yup';

import { Button, CaptchaTurnstile } from '@/components/Elements';
import { iSetupData } from '@/components/PublicPages/SetupPublicPage';
import { DEFAULT_LANG, DEFAULT_THEME, RECAPTCHA_TURNSTILE_SITE_KEY } from '@/config';
import { AppLanguages } from '@/types';
import {
  checkPhone,
  getFormName,
  renderField,
  renderLegalCheck,
  renderSelect,
} from '@/utils/forms';
import { getRegisterUrl, themes } from '@/utils/helpers';

const FormSetupWrapper = styled.div`
  .form--setup button[type='submit']:focus {
    outline: 2px solid var(--md-mediumGray-color);
  }
`;

const CheckboxConsentWrap = styled.div`
  #consentTerms:checked:after {
    color: var(--md-blue-color);
    content: '✓';
    font-size: 19px;
    left: 3px;
    position: absolute;
    top: -3px;
  }

  #consentTerms:focus {
    outline: 1px solid;
  }
`;

const SetupInfoMessage = styled.div`
  .setup--info__message {
    padding: 20px;
    background-color: #f6f6f4;
    margin-bottom: 30px;
    font-size: 14px;
    color: #666;
    border-left: 10px solid #eaeaea;
  }

  .setup--info__message a {
    color: var(--md-turquoise-color);
  }
`;

const SetupErrorMessage = styled.div`
  .setup--error__message {
    border-left: 10px solid red;
    background-color: #ff7a7a;
    color: #fff;
    padding: 20px;
    margin-bottom: 40px;
  }
`;

export type FormSetupProps = {
  initialValues: iSetupData;
  onSubmit: (values: iSetupData) => void;
  systemError: any;
  isSubmitting: boolean;
  showAgreementsCheck: boolean;
};

export const FormSetup = ({
  initialValues,
  onSubmit,
  systemError = {},
  isSubmitting = false,
  showAgreementsCheck = false,
}: FormSetupProps) => {
  const { t, i18n } = useTranslation();
  let formikGlobal: any;

  const defaultValidationSchema = {
    name: Yup.string()
      .max(40, `${t('Forms.maxErrorChars')} 40 ${t('Forms.chars')}`)
      .required(t('Forms.required')),
    lastname: Yup.string()
      .max(40, `${t('Forms.maxErrorChars')} 40 ${t('Forms.chars')}`)
      .required(t('Forms.required')),
    companyName: Yup.string().required(t('Forms.required')),
    internationalPhone: Yup.string()
      .min(3, `${t('Forms.minErrorChars')} 3 ${t('Forms.chars')}`)
      .test('test-phone', t('Forms.invalidFormat'), checkPhone)
      .required(t('Forms.required')),
    consentTerms: Yup.boolean(),
    recaptcha: Yup.string().required(t('Forms.required')),
  };

  const onFormSubmit = (values: iSetupData) => {
    onSubmit && onSubmit(values);
  };

  const SetupForm = ({ formik }: any) => {
    formikGlobal = formik;
    const renderConsentCheck = () => {
      return (
        <div className="mb-2 mt-2">
          <label className="mb-3 block" htmlFor="consentTerms">
            <div className="mb-[35px] flex items-start lg:items-center gap-4">
              <CheckboxConsentWrap>
                {renderLegalCheck(formik, 'consentTerms', {
                  renderLabel: () => t('Forms.Setup.consentTerms'),
                })}
              </CheckboxConsentWrap>
            </div>
          </label>
        </div>
      );
    };

    const getSystemErrors = () => {
      if (Object.keys(systemError).length) {
        const { detail, status } = systemError;
        const message =
          status === 418 ? t('Forms.Setup.Errors.418') : t(`Forms.Setup.Errors.${detail}`);

        return (
          <SetupErrorMessage>
            <div className="setup--error__message">
              <p className="text-sm">{message}</p>
            </div>
          </SetupErrorMessage>
        );
      }
    };

    return (
      <FormSetupWrapper>
        <form onSubmit={formik.handleSubmit} className="form--setup" id={getFormName(formik)}>
          {renderField(formik, 'hash', { type: 'hidden' })}
          {renderField(formik, 'email', { type: 'hidden' })}
          {renderField(formik, 'phone', { type: 'hidden' })}
          {renderField(formik, 'mobilePrefix', { type: 'hidden' })}
          {renderField(formik, 'recaptcha', {
            type: 'hidden',
            onChange: (value: string) => {
              formik.setFieldValue('recaptcha', value);
            },
          })}

          <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
            {renderField(formik, 'name', {
              customClasses: 'w-full md:w-1/2',
              required: true,
              autoFocus: !isSubmitting,
            })}
            {renderField(formik, 'lastname', { customClasses: 'w-full md:w-1/2', required: true })}
          </div>

          <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
            {renderField(formik, 'companyName', { customClasses: 'w-full', required: true })}
          </div>

          <div className="mt-10">
            <h5>{t('Forms.Setup.phone')}*</h5>
            <p className="text-md-softGray">{t('Forms.Setup.phoneDescription')}</p>

            <div className="mt-2 mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
              {renderSelect(formik, 'internationalPhone', {
                showLabel: false,
                customClasses: 'w-full',
                type: 'phone',
                required: true,
                renderLabel: (item: { name: string }) => item.name,
                defaultCountry: i18n.language
                  ? i18n.language.toUpperCase()
                  : DEFAULT_LANG.toUpperCase(),
                userLanguage: (i18n.language || DEFAULT_LANG) as AppLanguages,
                onChange: (phone: string, formValues: any, phoneData: PhoneNumber | undefined) => {
                  formik.setFieldValue('phone', phoneData?.nationalNumber);
                  formik.setFieldValue('mobilePrefix', `+${phoneData?.countryCallingCode}`);
                },
              })}
            </div>
            {showAgreementsCheck && renderConsentCheck()}
          </div>

          <div className="mt-5">
            {getSystemErrors()}
            <div className="mt-2 mb-3 flex justify-center lg:justify-between flex-wrap">
              <div className="flex items-center mb-10 lg:mb-0 flex-wrap justify-center lg:justify-start w-full lg:w-auto ">
                <Button
                  id={getFormName(formik) + 'Button'}
                  variant="primary"
                  type="submit"
                  size="free"
                  className="py-5 lg:py-3 w-full lg:w-[140px] focus:outline-black outline-2 outline-md-gray"
                  isLoading={isSubmitting}
                  loadingText={t('common.sending')}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormSetupWrapper>
    );
  };

  const MainForm = (props: any) => {
    const { className } = props;
    const formikData = {
      initialValues,
      validationSchema: Yup.object(defaultValidationSchema),
      onSubmit: onFormSubmit,
    };

    return (
      <div className={className}>
        <SetupForm formik={useFormik(formikData)} />
        <CaptchaTurnstile siteKey={RECAPTCHA_TURNSTILE_SITE_KEY} onVerify={onCaptchaChange} />
      </div>
    );
  };

  const EmailCheckInfo = () => {
    const { email } = initialValues;

    return (
      <SetupInfoMessage>
        <div className="setup--info__message">
          <p>
            {`${t('Forms.Setup.userMailCheck')} `}
            <span className="font-bold pl-2 font-secondary text-[13px]">{email}</span>
          </p>
          <p className="mt-0">
            {`${t('Forms.Setup.userMailCheck2')} `}
            <a
              href={getRegisterUrl(t('ProductsPublicPage.contractPath'))}
              title={t('Forms.Setup.userMailReturn')}
            >
              {t('Forms.Setup.userMailCheck3')}
            </a>
            .
          </p>
        </div>
      </SetupInfoMessage>
    );
  };

  const onCaptchaChange = (value: string | null) => {
    if (value) {
      setTimeout(() => {
        const captchaField = document.getElementById('recaptcha');
        captchaField?.setAttribute('value', value);
        formikGlobal.setFieldValue('recaptcha', value);
      }, 800);
    }
  };

  return (
    <div className="max-w-7xl mt-12 m-auto px-4 lg:px-0">
      <div className="max-w-3xl m-auto text-md-blue">
        <img
          alt="MDirector"
          className="mx-auto mb-10"
          src={themes[DEFAULT_THEME].assetsURL + 'globals/src/images/logos/logo_signup_small.png'}
        />
        <h3 className="md__payments__form--h3">{t('Forms.Setup.description')}</h3>
        <EmailCheckInfo />
        <MainForm />
      </div>
    </div>
  );
};
